import { useState, useEffect } from "react";
import "./TrackingDetail.css";
import ArrowRight from "../../assets/icons/ArrowRight.svg";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import ProfileLayout from "../../components/profilelayout/ProfileLayout";
import ApiService from "../../components/Api_services";
import FallbackImg from "../../components/FallbackImg";

const OrderTrackingDetail = () => {
    const { order_no } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
  

    const [trackingSteps, setTrackingSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleGoBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (!order_no) {
            setError("Order ID is missing");
            setLoading(false);
            return;
        }
        const fetchTrackingDetails = async () => {
            try {
            setLoading(true);
            const response = await ApiService.getOrderTrackingDetail(order_no);
            if (response && response.status === 200 && response.data) {
                processTrackingData(response.data);
            } else {
                setError("Failed to load tracking details");
                setTrackingSteps([]);
            }
            } catch (err) {
            setError("An error occurred while fetching tracking details");
            setTrackingSteps([]);
            } finally {
            setLoading(false);
            }
        };
        fetchTrackingDetails();
    }, [order_no]);

  // Helper to format dates
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00 00:00:00") return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Process tracking data from API
  const processTrackingData = (data) => {
    const steps = [];
    let currentStepIndex = 0;

    const isValidDate = (dateStr) => {
      if (!dateStr || dateStr === "0000-00-00 00:00:00") return false;
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    };

    if (data.inserted_date && isValidDate(data.inserted_date)) {
      steps.push({
        label: "Order Placed",
        date: formatDate(data.inserted_date),
        description: "Your order has been placed successfully.",
      });
      currentStepIndex++;
    }
    if (data.processing_date && isValidDate(data.processing_date)) {
      steps.push({
        label: "Processing",
        date: formatDate(data.processing_date),
        description: "Your order is being processed.",
      });
      currentStepIndex++;
    }
    if (data.shipping_date && isValidDate(data.shipping_date)) {
      steps.push({
        label: "Shipped",
        date: formatDate(data.shipping_date),
        description: "Your order has been shipped.",
      });
      currentStepIndex++;
    }
    if (data.received_date && isValidDate(data.received_date)) {
      steps.push({
        label: "Delivered",
        date: formatDate(data.received_date),
        description: "Your order has been delivered.",
      });
      currentStepIndex++;
    }
    if (data.cancel_date && isValidDate(data.cancel_date)) {
      steps.push({
        label: "Order Cancelled",
        date: formatDate(data.cancel_date),
        description: "Your order has been cancelled.",
      });
      currentStepIndex++;
    }

    setTrackingSteps(steps);
    setCurrentStep(currentStepIndex);
  };

  // Show a loading message while fetching data
  if (loading) {
    return (
      <ProfileLayout>
        <div className="trackingdetail">
          <div className="trackingdetail-content">
            <div className="trackingdetail-title-container">
              <div className="tdback-button" onClick={handleGoBack}>
                <FallbackImg
                  src={ArrowRight}
                  alt="arrowright"
                  width="20"
                  height="20"
                  style={{ display: "flex", cursor: "pointer" }}
                />
              </div>
              <div className="trackingdetail-title">
                <strong>TRACKING DETAILS</strong>
              </div>
            </div>
            <div className="trackingdetail-main">
              <p>Loading tracking details...</p>
            </div>
          </div>
        </div>
      </ProfileLayout>
    );
  }

  // Show an error message if something went wrong
  if (error || trackingSteps.length === 0) {
    return (
      <ProfileLayout>
        <div className="trackingdetail">
          <div className="trackingdetail-content">
            <div className="trackingdetail-title-container">
              <div className="tdback-button" onClick={handleGoBack}>
                <FallbackImg
                  src={ArrowRight}
                  alt="arrowright"
                  width="20"
                  height="20"
                  style={{ display: "flex", cursor: "pointer" }}
                />
              </div>
              <div className="trackingdetail-title">
                <strong>TRACKING DETAILS</strong>
              </div>
            </div>
            <div className="trackingdetail-main">
              <p>{error || "No tracking information available yet."}</p>
              {/* <Link to={`/orderdetail/${orderNo || orderId}`}>
                <button className="back-button">Back to Order Detail</button>
              </Link> */}
            </div>
          </div>
        </div>
      </ProfileLayout>
    );
  }

  const reversedSteps = [...trackingSteps].reverse();

  return (
    <ProfileLayout>
      <div className="trackingdetail">
        <div className="trackingdetail-content">
          <div className="trackingdetail-title-container">
            <div className="tdback-button" onClick={handleGoBack}>
              <FallbackImg
                src={ArrowRight}
                alt="arrowright"
                width="20"
                height="20"
                style={{ display: "flex", cursor: "pointer" }}
              />
            </div>
            <div className="trackingdetail-title">
              <strong>TRACKING DETAILS</strong>
            </div>
          </div>

          <div className="trackingdetail-main">
            <div className="vertical-progress-tracker">
              {reversedSteps.map((step, idx) => {
                const stepNum = trackingSteps.length - idx;
                return (
                  <div className="vpt-step" key={step.label}>
                    <div className="vpt-track">
                      <div
                        className={`vpt-circle${
                          currentStep === stepNum ? " active" : ""
                        }`}
                      ></div>
                      {idx < trackingSteps.length - 1 && (
                        <div className="vpt-line"></div>
                      )}
                    </div>
                    <div
                      className={`step-content${
                        currentStep === stepNum ? " textactive" : ""
                      }`}
                    >
                      <div className="step-container">
                        <div className="step">
                          <strong>{step.label}</strong>
                        </div>
                        <div className="step-date">{step.date}</div>
                      </div>
                      <div className="step-descrip">{step.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </ProfileLayout>
  );
};

export default OrderTrackingDetail;